import { Box } from "@mui/material";
import {
  DataGridPremium,
  GridApiPremium,
  GridColDef,
} from "@mui/x-data-grid-premium";
import { FC, RefObject, useMemo } from "react";
import dayjs from "dayjs";
import { useMessages } from "../../../../Services/Nats";
import { tradeKey } from "../../../../Services/nats/virtualTrade";
import { find } from "../../../orderBook/allGrids";
import { formatDeliveryWindow } from "../../../../deliveryWindow";
import {
  formatMegawatts,
  kilowattsToMegawatts,
  QUANTITY_UNIT,
} from "../../../../megawatts";
import { EXCHANGE_TIMEZONE } from "../../../../dayjsSetup";
import { TradeDirection } from "../shared/tradeDirection";
import { gridStyle } from "../shared/gridStyle";
import { useSelectedArea } from "../../atoms/selectedGrid";
import { ALL_GRIDS } from "../shared/constants";
import { Price, PRICE_UNIT } from "../../../../price";

type TradeRow = {
  id: string;
  executionTime: string;
  grid: string;
  product: string;
  direction: string;
  quantityMw: number | undefined;
  price: Price | null;
  algoId: string;
  tradeId: string;
  orderId: string;
  externalOrderId: string;
  text: string;
};

const cols: GridColDef<TradeRow>[] = [
  {
    field: "executionTime",
    headerName: "Executed (JST)",
    width: 170,
    // With the year: the CSV export writes the formatted value, and the tape
    // can span a year boundary.
    valueFormatter: (value: string) =>
      dayjs(value).tz(EXCHANGE_TIMEZONE).format("YYYY-MM-DD HH:mm:ss"),
  },
  { field: "grid", headerName: "Grid", width: 90 },
  { field: "product", headerName: "Product", width: 145 },
  {
    field: "direction",
    headerName: "B/S",
    width: 50,
    align: "center",
    headerAlign: "center",
    renderCell: (params) => <TradeDirection tradeDirection={params.value} />,
  },
  {
    field: "quantityMw",
    headerName: `Quantity (${QUANTITY_UNIT})`,
    width: 110,
    type: "number",
    valueFormatter: (value: number | undefined) =>
      formatMegawatts(value) ?? "-",
  },
  {
    // valueGetter so the grid sorts and filters on the number: DataGrid cannot
    // order a `Price`. The row keeps the Price itself.
    field: "price",
    headerName: `Price (${PRICE_UNIT})`,
    width: 110,
    type: "number",
    valueGetter: (_value, row: TradeRow) => row.price?.yenPerKwh ?? null,
    valueFormatter: (value: number | null) =>
      value === null ? "-" : String(value),
  },
  { field: "algoId", headerName: "Algo Id", width: 290 },
  { field: "tradeId", headerName: "Trade Id", width: 100 },
  { field: "orderId", headerName: "Order Id", width: 290 },
  { field: "externalOrderId", headerName: "Exchange Id", width: 110 },
  { field: "text", headerName: "Information", width: 400 },
];

export const TradesDatagrid: FC<{
  quickFilterValues?: string[];
  /** Held by the view toolbar, which owns the CSV export button. */
  apiRef: RefObject<GridApiPremium | null>;
}> = ({ quickFilterValues, apiRef }) => {
  const { virtualTrades, contracts } = useMessages();
  const [selectedArea] = useSelectedArea();

  const rows = useMemo(() => {
    const result: TradeRow[] = [];

    for (const trade of Object.values(virtualTrades)) {
      const { delivery_area_id, contract_id } = trade.contract;
      const grid = find(delivery_area_id).displayName;

      // The algos grid gets this filter server-side via the `eic` param, but
      // trades arrive over NATS for every area, so it is applied here.
      if (selectedArea !== ALL_GRIDS && grid !== selectedArea) {
        continue;
      }

      result.push({
        // The store's key, not the bare trade id: the two legs of a self-trade
        // share an id, and duplicate row ids make the grid drop one.
        id: tradeKey(trade),
        executionTime: trade.execution_time,
        grid,
        product: productLabel(contracts, delivery_area_id, String(contract_id)),
        direction: trade.direction,
        quantityMw: kilowattsToMegawatts(trade.quantity?.kilo_watts),
        // Converted at ingest; see parseVirtualTrade.
        price: trade.price,
        algoId: trade.algo_id,
        tradeId: trade.trade_id,
        // The id the venue was given, not `internal_client_order_id` — this is
        // the one that shows up in lazerhub and on the exchange.
        orderId: trade.external_client_order_id?.inner ?? "",
        externalOrderId: trade.exchange_order_id,
        text: trade.external_order_text,
      });
    }

    return result;
  }, [virtualTrades, contracts, selectedArea]);

  return (
    <Box position="relative" flex={1} height={"100%"}>
      <DataGridPremium
        apiRef={apiRef}
        sx={gridStyle}
        rows={rows}
        columns={cols}
        rowHeight={28}
        density="compact"
        initialState={{
          sorting: { sortModel: [{ field: "executionTime", sort: "desc" }] },
        }}
        filterModel={{ items: [], quickFilterValues }}
        disableColumnSelector
        disableDensitySelector
        disableColumnMenu
        hideFooter
      />
    </Box>
  );
};

/**
 * A trade carries only its contract id, so the delivery window is joined from
 * the contracts feed. Contracts are pruned once delivery has passed, and the
 * tape reaches a week back, so most rows land on the fallback: read the id as
 * the delivery-start epoch, which is what it is on this venue
 * (`contracts.jepx.<area>.<epoch_ms>`).
 */
const productLabel = (
  contracts: ReturnType<typeof useMessages>["contracts"],
  areaId: string,
  contractId: string
): string => {
  const contract = contracts[areaId]?.[contractId];
  if (contract !== undefined) {
    return (
      formatDeliveryWindow(contract.delivery_start, contract.delivery_end) ??
      contract.name
    );
  }

  const start = dayjs(Number(contractId));
  return start.isValid()
    ? start.tz(EXCHANGE_TIMEZONE).format("MM-DD HH:mm")
    : contractId;
};
